'use client';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';

import { FOCUS_STYLES } from '@ui/lib/focus-styles';
import { cn } from '@ui/lib/utils';
import { VisuallyHidden } from './visually-hidden';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-black/40 backdrop-blur-md hover:backdrop-blur-none transition-all duration-\[320ms\] ease-in data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 group',
      className
    )}
    {...props}
    ref={ref}
  >
    <div className="absolute inset-0 flex items-center justify-center">
      <button
        className={cn(
          'flex items-center gap-2 rounded-full glassmorphism-neutral px-4 py-2.5 transition-all duration-200 ease-in group-hover:opacity-100 opacity-0 hover:scale-105',
          FOCUS_STYLES.overlay
        )}
      >
        <div className="flex h-6 w-6 items-center justify-center rounded-full glassmorphism-light">
          <X className="h-3.5 w-3.5 text-slate-800" />
        </div>
        <span className="text-sm font-bold drop-shadow-sm">Click to Close</span>
      </button>
    </div>
  </SheetPrimitive.Overlay>
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  'fixed z-50 gap-2 bg-background shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 p-0',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        bottom:
          'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        left: 'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
        right:
          'inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  }
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  onCloseClick?: () => void;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = 'right', className, children, onCloseClick, ...props }, ref) => {
  // Simple check for any SheetTitle in children
  const hasSheetTitle = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type === SheetTitle
  );

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        {/* Add a default title if one isn't provided */}
        {!hasSheetTitle && (
          <VisuallyHidden asChild>
            <SheetTitle>Sheet</SheetTitle>
          </VisuallyHidden>
        )}
        {children}
        <SheetPrimitive.Close
          className={cn(
            'absolute right-3 top-3 rounded-lg p-1 ring-1 ring-slate-200/65 ring-offset-background transition-opacity hover:opacity-100 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground',
            FOCUS_STYLES.close
          )}
          onClick={onCloseClick}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col text-left sm:text-left shadow-xs px-6 py-4 pb-2',
      className
    )}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-start sm:space-x-2 px-6 py-4',
      className
    )}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn('text-lg font-bold text-foreground mb-1', className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground mt-1', className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

// New component for standardized button ribbon
const SheetButtonRibbon = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'sticky bottom-0 flex justify-start gap-2 bg-white px-0 py-6 shadow-[inset_0_-2px_2px_rgba(0,0,0,0.02)]',
      // When viewport width < 560px, stack buttons vertically in reverse order
      'max-[559px]:flex-col-reverse [&>*]:max-[559px]:w-full max-[559px]:gap-2',
      // Make buttons with data-primary="true" flex-grow, while others stay normal width
      // Add min-width and text overflow handling for primary buttons
      '[&>*[data-primary="true"]]:flex-grow [&>*[data-primary="true"]]:min-w-[180px] [&>*[data-primary="true"]]:whitespace-normal',
      className
    )}
    {...props}
  />
);
SheetButtonRibbon.displayName = 'SheetButtonRibbon';

export {
  Sheet,
  SheetButtonRibbon,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
